import { FC } from 'react';
import { Link } from 'react-router-dom';
import { useRelatorios } from '@/hooks/useRelatorios';
import type { ItemRelatorio } from '@/config/relatorios';

const CardRelatorio: FC<{ item: ItemRelatorio }> = ({ item }) => {
  const miolo = (
    <>
      <span
        aria-hidden
        className="material-symbols-outlined text-[22px] leading-none text-accent"
      >
        {item.icone}
      </span>
      <span className="min-w-0">
        <span className="flex flex-wrap items-center gap-2 font-semibold text-content-primary">
          {item.rotulo}
          {item.emBreve && (
            <span className="whitespace-nowrap rounded bg-surface-tertiary px-1.5 py-0.5 text-2xs uppercase tracking-wide text-content-muted">
              em breve
            </span>
          )}
        </span>
        <span className="mt-1 block text-sm text-content-secondary">{item.descricao}</span>
      </span>
    </>
  );

  const molde = 'flex items-start gap-3 rounded-xl border border-theme-border bg-surface-card p-5';

  if (item.emBreve) {
    return (
      <li className={`${molde} opacity-60`} data-testid={`relatorio-${item.rota}`}>
        {miolo}
      </li>
    );
  }

  return (
    <li>
      <Link
        to={item.rota}
        data-testid={`relatorio-${item.rota}`}
        className={`${molde} h-full transition-colors hover:border-accent hover:bg-surface-hover`}
      >
        {miolo}
      </Link>
    </li>
  );
};

/**
 * Home dos relatórios (`/relatorios`).
 *
 * Índice de tudo que a pessoa pode abrir, agrupado por área. Quem chega aqui já
 * passou pelo `RelatoriosRoute`, então a lista nunca vem vazia — mas ela pode vir
 * parcial, porque cada frente tem a sua liberação.
 */
const RelatoriosHomePage: FC = () => {
  const { grupos } = useRelatorios();

  return (
    <div className="flex flex-col gap-8 p-6 lg:p-10">
      <header>
        <p className="text-xs uppercase tracking-wide text-content-muted">h.lab</p>
        <h1 className="text-2xl font-bold text-content-primary">Relatórios</h1>
        <p className="mt-1 max-w-2xl text-sm text-content-secondary">
          Os painéis e ferramentas de planejamento da operação. O que você não estiver vendo aqui
          é liberação de acesso — fale com seu admin.
        </p>
      </header>

      {grupos.map((grupo) => (
        <section key={grupo.id} data-testid={`grupo-${grupo.id}`}>
          <h2 className="font-semibold text-content-primary">{grupo.titulo}</h2>
          <p className="mt-0.5 text-sm text-content-muted">{grupo.descricao}</p>
          <ul className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {grupo.itens.map((item) => (
              <CardRelatorio key={item.rota} item={item} />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
};

export default RelatoriosHomePage;
